/**
 * Reglas de edición de datos del participante (§12.6, RF-23).
 */
import { DatosCiclo, ParticipanteEntity, TipoDocumento } from '../models/asistencia.model';
import { Evaluacion, Hallazgo, hallazgo } from '../models/errores';

export type CampoEditable = 'tipoDocumento' | 'numeroDocumento' | 'nombres' | 'apellidos' | 'correo' | 'celular';

export const CAMPOS_EDITABLES: CampoEditable[] = ['tipoDocumento', 'numeroDocumento', 'nombres', 'apellidos', 'correo', 'celular'];
export const CAMPOS_SENSIBLES: CampoEditable[] = ['tipoDocumento', 'numeroDocumento', 'nombres', 'apellidos', 'correo'];

export type DatosEditables = Pick<ParticipanteEntity, CampoEditable>;

const RE_CORREO = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[a-z]{2,}$/i;

export function correoValido(correo: string): boolean {
  return RE_CORREO.test(correo.trim());
}

export function celularValido(celular: string | undefined): boolean {
  return !celular || /^3\d{9}$/.test(celular.trim());
}

export function documentoValido(tipo: TipoDocumento, numero: string): boolean {
  return tipo === 'PASAPORTE' || tipo === 'PPT' ? /^[A-Z0-9]{5,15}$/i.test(numero) : /^\d{5,12}$/.test(numero);
}

export function normalizarDatos(d: DatosEditables): DatosEditables {
  return {
    tipoDocumento: d.tipoDocumento,
    numeroDocumento: d.numeroDocumento.trim().toUpperCase(),
    nombres: d.nombres.trim().replace(/\s+/g, ' '),
    apellidos: d.apellidos.trim().replace(/\s+/g, ' '),
    correo: d.correo.trim().toLowerCase(),
    celular: d.celular?.trim() || undefined
  };
}

export function camposModificados(original: DatosEditables, nuevo: DatosEditables): CampoEditable[] {
  return CAMPOS_EDITABLES.filter(c => (original[c] ?? '') !== (nuevo[c] ?? ''));
}

export function validarDatos(datos: DatosCiclo, participanteId: string | null, d: DatosEditables): Hallazgo[] {
  const errores: Hallazgo[] = [];
  if (!d.nombres || !d.apellidos) errores.push(hallazgo('PART-007'));
  if (!documentoValido(d.tipoDocumento, d.numeroDocumento)) errores.push(hallazgo('IMP-002'));
  if (!correoValido(d.correo)) errores.push(hallazgo('PART-002'));
  if (!celularValido(d.celular)) errores.push(hallazgo('PART-003'));
  if (datos.participantes.some(p => p.id !== participanteId && p.numeroDocumento === d.numeroDocumento)) errores.push(hallazgo('PART-001'));
  return errores;
}

export function evaluarEdicion(
  datos: DatosCiclo,
  original: ParticipanteEntity,
  propuesta: DatosEditables,
  versionEsperada: number,
  motivo?: string
): Evaluacion & { campos: CampoEditable[] } {
  const nuevo = normalizarDatos(propuesta);
  const campos = camposModificados(original, nuevo);
  const bloqueos = validarDatos(datos, original.id, nuevo);
  if (original.version !== versionEsperada) bloqueos.unshift(hallazgo('PART-004'));
  if (campos.some(c => CAMPOS_SENSIBLES.includes(c)) && !motivo?.trim()) bloqueos.push(hallazgo('PART-005'));
  if (!campos.length) bloqueos.push(hallazgo('GEN-001', 'No hay cambios para guardar.'));
  return { bloqueos, advertencias: [], campos };
}
